import { useState } from 'react'
import { Link } from 'react-router'
import { Button, Card, IconButton, Input, SectionLabel } from '../../../design/components'
import { hasCity, type CitySection } from '../types'
import type { CampaignEditing } from '../useCampaignEditing'
import styles from './sections.module.css'

/**
 * The city — design spec §5.4's "city as a character".
 *
 * The same card serves both screens, because the same section serves both reads:
 * `GET /campaigns/{id}` carries the stub with its counts, `GET /campaigns/{id}/city`
 * carries the identical shape alongside the factions and locations. On the campaign
 * page it is a doorway (`linkTo`); on the city page it is the header.
 *
 * A campaign with no city yet answers with the section present and every field null,
 * which is the signal to offer "create city" — not an absent section, and not an
 * error.
 */
export function CityCard({
  city,
  editing,
  linkTo,
}: {
  city: CitySection | undefined
  editing: CampaignEditing
  /** Renders a link into the world view. Omitted on the world view itself. */
  linkTo?: string | undefined
}) {
  const [open, setOpen] = useState(false)

  if (city === undefined) return null

  const canEdit = editing.access.isGameMaster
  const exists = hasCity(city)

  if (canEdit && open) {
    return (
      <CityForm city={exists ? city : undefined} editing={editing} onClose={() => setOpen(false)} />
    )
  }

  if (!exists) {
    return (
      <Card
        variant="aspects"
        label="The City"
        action={
          canEdit ? (
            <IconButton label="Create the city" icon="add" onClick={() => setOpen(true)} />
          ) : undefined
        }
      >
        <p className={styles.empty}>
          {canEdit
            ? 'This campaign has no city yet. Factions and places hang off one.'
            : 'The Game Master has not built a city for this campaign yet.'}
        </p>
      </Card>
    )
  }

  return (
    <Card
      variant="aspects"
      label="The City"
      hint={city.name ?? undefined}
      action={
        canEdit ? (
          <IconButton label="Edit the city" icon="edit" onClick={() => setOpen(true)} />
        ) : undefined
      }
    >
      <div className={styles.namedLine}>
        <SectionLabel tone="muted" micro>
          Theme
        </SectionLabel>
        {city.theme !== null && city.theme.trim() !== '' ? (
          <span className={styles.namedLineText}>{city.theme}</span>
        ) : (
          <span className={styles.empty}>Not set</span>
        )}
      </div>

      {city.description !== null && city.description.trim() !== '' && (
        <div className={styles.namedLine}>
          <SectionLabel tone="muted" micro>
            Description
          </SectionLabel>
          <span className={styles.rowText}>{city.description}</span>
        </div>
      )}

      <div className={styles.meta}>
        <span className={styles.metaItem}>
          <SectionLabel tone="muted" micro>
            Factions
          </SectionLabel>
          <span className={styles.metaValue}>{city.factionCount}</span>
        </span>
        <span className={styles.metaItem}>
          <SectionLabel tone="muted" micro>
            Places
          </SectionLabel>
          <span className={styles.metaValue}>{city.locationCount}</span>
        </span>
      </div>

      {linkTo !== undefined && (
        <div className={styles.actions}>
          <Link to={linkTo} className={styles.cardLink}>
            <Button variant="secondary" fullWidth>
              Enter {city.name}
            </Button>
          </Link>
        </div>
      )}
    </Card>
  )
}

/**
 * Create and edit share a form because they share a body (`CityRequest`). Delete
 * lives here too, and is worth a confirmation the rest of this screen does not need:
 * the city cascades, taking every faction and location with it.
 */
function CityForm({
  city,
  editing,
  onClose,
}: {
  city?: CitySection | undefined
  editing: CampaignEditing
  onClose: () => void
}) {
  const [name, setName] = useState(city?.name ?? '')
  const [theme, setTheme] = useState(city?.theme ?? '')
  const [description, setDescription] = useState(city?.description ?? '')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const creating = city === undefined
  const pending = editing.createCity.isPending || editing.updateCity.isPending
  const deleting = editing.removeCity.isPending

  return (
    <Card variant="aspects" label={creating ? 'Create the city' : 'Edit the city'}>
      <form
        className={styles.inlineForm}
        style={{ marginTop: 0 }}
        onSubmit={(event) => {
          event.preventDefault()
          const body = {
            name,
            theme: theme === '' ? null : theme,
            description: description === '' ? null : description,
          }
          const mutation = creating ? editing.createCity : editing.updateCity
          mutation.mutate(body, { onSuccess: onClose })
        }}
      >
        <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />

        <label className={styles.field}>
          <SectionLabel tone="muted" micro>
            Theme
          </SectionLabel>
          <textarea
            className={styles.textarea}
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
          />
        </label>

        <label className={styles.field}>
          <SectionLabel tone="muted" micro>
            Description
          </SectionLabel>
          <textarea
            className={styles.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className={styles.actions}>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending || name.trim() === ''}>
            {pending ? 'Saving…' : creating ? 'Create' : 'Save'}
          </Button>
        </div>
      </form>

      {!creating && (
        <div className={styles.actions}>
          {confirmingDelete ? (
            <>
              <p className={styles.note}>
                Deleting the city removes every faction and place in it. This cannot be undone.
              </p>
              <Button variant="secondary" onClick={() => setConfirmingDelete(false)}>
                Keep it
              </Button>
              <Button
                variant="danger"
                disabled={deleting}
                onClick={() => editing.removeCity.mutate(undefined, { onSuccess: onClose })}
              >
                {deleting ? 'Deleting…' : 'Delete the city'}
              </Button>
            </>
          ) : (
            <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
              Delete the city
            </Button>
          )}
        </div>
      )}
    </Card>
  )
}
